import axios, { AxiosResponse } from 'axios';
import {
  User,
  Candidate,
  Evaluation,
  PIProfile,
  PositionTemplate,
  Job,
  LoginData,
  RegisterData,
  DashboardData,
  AnalyticsData,
  PaginationParams,
  Team,
  TeamMember,
  TeamDiscoveryResult,
  StrategyTypeKey,
} from '../types';

// Use relative URL by default to work with any domain (staging, production, etc.)
// This allows the frontend to always use the same domain it's served from
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Track if we're currently refreshing to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry && originalRequest.url !== '/auth/refresh') {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await api.post('/auth/refresh', { refreshToken });
        const { token } = response.data;
        localStorage.setItem('token', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        processQueue(null, token);
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export interface AuthResponse {
  message: string;
  user: User;
  token: string;
  refreshToken?: string;
}

export interface ActiveUser {
  id: number;
  first_name: string;
  last_name: string;
  name: string;
  email: string;
  role: User['role'];
}

export interface InternalProfile {
  id: string;
  internal_person_id?: number;
  person_id?: string | null;
  external_person_id?: string | null;
  first_name: string;
  last_name: string;
  name: string;
  email: string;
  role_title: string;
  reference_profile?: string | null;
  behavioral_assessment_id?: string | null;
  assessment_state?: number | null;
  assessment_state_description?: string | null;
  has_completed_behavioral_assessment: boolean;
  pi_factor_a?: number | null;
  pi_factor_b?: number | null;
  pi_factor_c?: number | null;
  pi_factor_d?: number | null;
  source?: string | null;
  candidate_id?: number | null;
  last_synced_at?: string | null;
}

export interface InternalTeamEmail {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const authApi = {
  login: (data: LoginData): Promise<AxiosResponse<AuthResponse>> =>
    api.post('/auth/login', data),
  
  register: (data: RegisterData): Promise<AxiosResponse<AuthResponse>> =>
    api.post('/auth/register', data),
  
  getProfile: (): Promise<AxiosResponse<{ user: User }>> =>
    api.get('/auth/me'),
  
  changePassword: (data: { currentPassword: string; newPassword: string }): Promise<AxiosResponse<{ message: string }>> =>
    api.put('/auth/change-password', data),
  
  logout: (data: { refreshToken: string }): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/auth/logout', data),
  
  requestPasswordReset: (email: string): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/auth/request-password-reset', { email }),
  
  resetPassword: (token: string, newPassword: string): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/auth/reset-password', { token, newPassword }),
};

export const candidatesApi = {
  getAll: (params?: PaginationParams): Promise<AxiosResponse<{ candidates: Candidate[]; pagination: any }>> =>
    api.get('/candidates', { params }),
  
  getById: (id: string): Promise<AxiosResponse<Candidate>> =>
    api.get(`/candidates/${id}`),
  
  create: (data: FormData | Partial<Candidate>): Promise<AxiosResponse<{ message: string; candidate: Candidate }>> => {
    const isFormData = data instanceof FormData;
    return api.post('/candidates', data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
    });
  },
  
  import: (data: Partial<Candidate>): Promise<AxiosResponse<{ message: string; candidate: Candidate }>> =>
    api.post('/candidates/import', data),
  
  warmupImport: (): Promise<AxiosResponse<{ status: string }>> =>
    api.get('/candidates/import/warmup'),
  
  update: (id: string, data: FormData): Promise<AxiosResponse<{ message: string; candidate: Candidate }>> =>
    api.put(`/candidates/${id}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  
  updateStatus: (id: string, status: string): Promise<AxiosResponse<{ message: string; candidate: Candidate }>> =>
    api.patch(`/candidates/${id}/status`, { status }),
  
  delete: (id: string): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/candidates/${id}`),

  uploadDocuments: (id: string, data: FormData): Promise<AxiosResponse<{ message: string; candidate: Candidate }>> =>
    api.post(`/candidates/${id}/documents`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // Bulk document upload - analyze files and match to candidates
  bulkAnalyze: (data: FormData): Promise<AxiosResponse<{
    totalFiles: number;
    matched: number;
    unmatched: number;
    files: Array<{
      tempFilename: string;
      originalFilename: string;
      size: number;
      documentType: 'resume' | 'coverLetter';
      parsed: { firstName: string | null; lastName: string | null; confidence: number };
      match: {
        candidateId: number;
        candidateName: string;
        confidence: number;
        hasExisting: boolean;
      } | null;
    }>;
    candidates: Array<{ id: number; name: string; email: string }>;
  }>> =>
    api.post('/candidates/documents/bulk-analyze', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // Bulk document upload - confirm and save assignments
  bulkConfirm: (assignments: Array<{
    tempFilename: string;
    candidateId: number | null;
    documentType: 'resume' | 'coverLetter';
  }>): Promise<AxiosResponse<{
    message: string;
    results: {
      success: Array<{ tempFilename: string; candidateId: number; candidateName: string; documentType: string }>;
      skipped: Array<{ tempFilename: string; reason: string }>;
      errors: Array<{ tempFilename: string; candidateId: number; error: string }>;
    };
  }>> =>
    api.post('/candidates/documents/bulk-confirm', { assignments }),
};

export const evaluationsApi = {
  getAll: (): Promise<AxiosResponse<{ evaluations: Evaluation[] }>> =>
    api.get('/evaluations'),
  
  create: (data: Partial<Evaluation>): Promise<AxiosResponse<{ message: string; evaluation: Evaluation }>> =>
    api.post('/evaluations', data),
  
  update: (id: string, data: Partial<Evaluation>): Promise<AxiosResponse<{ message: string; evaluation: Evaluation }>> =>
    api.put(`/evaluations/${id}`, data),
};

export const piProfilesApi = {
  getAll: (): Promise<AxiosResponse<{ profiles: PIProfile[] }>> =>
    api.get('/pi-profiles'),
  
  getById: (id: string): Promise<AxiosResponse<{ profile: PIProfile }>> =>
    api.get(`/pi-profiles/${id}`),
};

export const positionTemplatesApi = {
  getAll: (): Promise<AxiosResponse<{ templates: PositionTemplate[] }>> =>
    api.get('/position-templates'),
  
  create: (data: Partial<PositionTemplate>): Promise<AxiosResponse<{ message: string; template: PositionTemplate }>> =>
    api.post('/position-templates', data),
};

export const usersApi = {
  getAll: (): Promise<AxiosResponse<{ users: User[] }>> =>
    api.get('/users'),

  getActive: (): Promise<AxiosResponse<ActiveUser[]>> =>
    api.get('/users/active'),

  getInternalTeamEmails: (): Promise<AxiosResponse<{ emails: InternalTeamEmail[] }>> =>
    api.get('/users/internal-team-emails'),

  addInternalTeamEmail: (
    payload: { email: string; first_name?: string; last_name?: string }
  ): Promise<AxiosResponse<{ message: string; email: InternalTeamEmail }>> =>
    api.post('/users/internal-team-emails', payload),

  updateInternalTeamEmail: (
    id: number | string,
    payload: { email: string; first_name?: string; last_name?: string }
  ): Promise<AxiosResponse<{ message: string; email: InternalTeamEmail }>> =>
    api.put(`/users/internal-team-emails/${id}`, payload),

  removeInternalTeamEmail: (id: number | string): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/users/internal-team-emails/${id}`),

  getInternalProfiles: (): Promise<AxiosResponse<InternalProfile[]>> =>
    api.get('/users/internal-profiles'),

  getInternalPeople: (): Promise<AxiosResponse<InternalProfile[]>> =>
    api.get('/users/internal-people'),

  syncInternalPeople: (): Promise<AxiosResponse<{
    message: string;
    status?: 'success' | 'partial_failure';
    partial_failure?: boolean;
    roster_count: number;
    synced_count: number;
    missing_count: number;
    error_count: number;
    score_enriched_count?: number;
    profile_estimated_count?: number;
    role_enriched_count?: number;
    unresolved_placeholders?: Array<{ id: number; first_name: string; last_name: string }>;
  }>> =>
    api.post('/users/internal-people/sync'),

  resolvePlaceholders: (
    resolutions: Array<{ id: number; email: string }>
  ): Promise<AxiosResponse<{
    results: Array<{ id: number; success: boolean; email?: string; error?: string }>;
  }>> =>
    api.post('/users/internal-people/resolve-placeholders', resolutions),

  importInternalReports: (
    reports: File[] | FileList
  ): Promise<AxiosResponse<{
    message: string;
    status?: 'success' | 'partial_failure';
    partial_failure?: boolean;
    uploaded_files: number;
    upload_file_limit?: number;
    pdf_reports_found: number;
    pdf_reports_relevant?: number;
    pdf_reports_selected?: number;
    pdf_reports_ignored?: number;
    score_extracted_count: number;
    imported_count: number;
    auto_created_count?: number;
    unresolved_count: number;
    manual_mappable_count?: number;
    imported_samples?: Array<{ filename: string; email: string; internal_person_id?: number | null; auto_created?: boolean }>;
    unresolved_samples?: Array<{
      filename: string;
      reason: string;
      parsed_name?: string | null;
      candidates?: string[];
      suggested_emails?: string[];
      score_payload?: {
        pi_factor_a?: number | null;
        pi_factor_b?: number | null;
        pi_factor_c?: number | null;
        pi_factor_d?: number | null;
      };
    }>;
  }>> => {
    const formData = new FormData();
    Array.from(reports || []).forEach((file) => {
      formData.append('reports', file);
    });
    return api.post('/users/internal-people/import-reports', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  manualAssociateInternalReports: (
    associations: Array<{
      filename: string;
      email: string;
      parsed_name?: string | null;
      score_payload: {
        pi_factor_a: number;
        pi_factor_b: number;
        pi_factor_c: number;
        pi_factor_d: number;
      };
    }>
  ): Promise<AxiosResponse<{
    message: string;
    status?: 'success' | 'partial_failure';
    partial_failure?: boolean;
    imported_count: number;
    unresolved_count: number;
    imported_samples?: Array<{ filename: string; email: string; internal_person_id?: number | null }>;
    unresolved_samples?: Array<{ filename: string; reason: string; provided_email?: string | null }>;
  }>> =>
    api.post('/users/internal-people/import-reports/manual-associations', { associations }),

  resolveInternalProfilesMembers: (profiles: InternalProfile[]): Promise<AxiosResponse<{
    members: Partial<TeamMember>[];
    with_pi_scores: number;
  }>> =>
    api.post('/users/internal-profiles/resolve-members', { profiles }),
};

export const jobsApi = {
  getAll: (params?: { status?: string; [key: string]: any }): Promise<AxiosResponse<{ jobs: Job[] }>> =>
    api.get('/jobs', { params }),
  
  getById: (id: string): Promise<AxiosResponse<Job>> =>
    api.get(`/jobs/${id}`),
  
  create: (data: Partial<Job>): Promise<AxiosResponse<{ message: string; job: Job }>> =>
    api.post('/jobs', data),
  
  update: (id: string, data: Partial<Job>): Promise<AxiosResponse<{ message: string; job: Job }>> =>
    api.put(`/jobs/${id}`, data),
  
  delete: (id: string): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/jobs/${id}`),
};

export const reportsApi = {
  getDashboard: (): Promise<AxiosResponse<DashboardData>> =>
    api.get('/reports/dashboard'),
  
  getAnalytics: (): Promise<AxiosResponse<AnalyticsData>> =>
    api.get('/reports/analytics'),
};

export interface Note {
  id: number;
  candidate_id: number;
  user_id: number;
  content: string;
  category?: string;
  is_private: boolean;
  is_pinned: boolean;
  attachments?: any[];
  created_at: string;
  updated_at: string;
  author_first_name: string;
  author_last_name: string;
  author_email: string;
  author_avatar?: string;
  mentions?: {
    user_id: number;
    name: string;
    email: string;
    is_read: boolean;
    read_at?: string;
  }[];
}

export interface Mention {
  id: number;
  note_id: number;
  mentioned_user_id: number;
  is_read: boolean;
  read_at?: string;
  created_at: string;
  updated_at: string;
  content: string;
  category: string;
  note_created_at: string;
  candidate_id: number;
  candidate_first_name: string;
  candidate_last_name: string;
  author_first_name: string;
  author_last_name: string;
}

export const notesApi = {
  // Get all notes for a candidate
  getCandidateNotes: (candidateId: string, params?: { category?: string; includePrivate?: boolean }): Promise<AxiosResponse<Note[]>> =>
    api.get(`/candidates/${candidateId}/notes`, { params }),
  
  // Create a new note
  createNote: (candidateId: string, data: {
    content: string;
    category?: string;
    is_private?: boolean;
    is_pinned?: boolean;
    mentions?: number[];
  }): Promise<AxiosResponse<Note>> =>
    api.post(`/candidates/${candidateId}/notes`, data),
  
  // Update a note
  updateNote: (noteId: string, data: {
    content?: string;
    category?: string;
    is_private?: boolean;
    is_pinned?: boolean;
  }): Promise<AxiosResponse<Note>> =>
    api.put(`/notes/${noteId}`, data),
  
  // Delete a note
  deleteNote: (noteId: string): Promise<AxiosResponse<void>> =>
    api.delete(`/notes/${noteId}`),
  
  // Get user mentions
  getUserMentions: (params?: { unreadOnly?: boolean }): Promise<AxiosResponse<Mention[]>> =>
    api.get('/mentions', { params }),
  
  // Mark mention as read
  markMentionRead: (mentionId: string): Promise<AxiosResponse<{ success: boolean }>> =>
    api.put(`/mentions/${mentionId}/read`),
};

export const emailLogsApi = {
  // Get email logs with filtering
  getAll: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    email_type?: string;
    search?: string;
    to_email?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<AxiosResponse<{
    logs: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }>> => api.get('/email-logs', { params }),
  
  // Get email log by ID
  getById: (id: string): Promise<AxiosResponse<any>> =>
    api.get(`/email-logs/${id}`),
  
  // Resend email
  resend: (id: number): Promise<AxiosResponse<{ message: string; result: any }>> =>
    api.post(`/email-logs/${id}/resend`),
  
  // Send test email
  sendTest: (data: { to_email: string; test_type: string }): Promise<AxiosResponse<{ message: string; result: any }>> =>
    api.post('/email-logs/test', data),
  
  // Get email statistics
  getStats: (days: number = 30): Promise<AxiosResponse<any>> =>
    api.get('/email-logs/stats/summary', { params: { days } }),
  
  // Cleanup old logs
  cleanup: (data: { days: number; dry_run: boolean }): Promise<AxiosResponse<any>> =>
    api.delete('/email-logs/cleanup', { data }),
};

// PI Sync Types
export interface PICandidatePreview {
  piPersonId: string;
  firstName: string;
  lastName: string;
  email: string;
  assessmentComplete: boolean;
  matchStatus: 'linked' | 'suggested' | 'none';
  localCandidate: {
    id: number;
    name: string;
  } | null;
}

export interface PIJobPreview {
  piJobId: string;
  name: string;
  targetFactorRanges: {
    aSigmaRangeLow?: number;
    aSigmaRangeHigh?: number;
    bSigmaRangeLow?: number;
    bSigmaRangeHigh?: number;
    cSigmaRangeLow?: number;
    cSigmaRangeHigh?: number;
    dSigmaRangeLow?: number;
    dSigmaRangeHigh?: number;
  } | null;
  matchStatus: 'linked' | 'suggested' | 'none';
  localJob: {
    id: number;
    title: string;
    department: string;
    status: string;
  } | null;
  candidateCount: number;
  candidates: PICandidatePreview[];
}

export interface PICandidateDecision {
  piPersonId: string;
  action: 'import' | 'skip';
}

export interface PIJobDecision {
  piJobId: string;
  action: 'link' | 'create' | 'update' | 'unlink' | 'skip';
  localJobId?: number;
  candidates?: PICandidateDecision[];
}

export interface PIJobSyncResults {
  jobs: {
    linked: number;
    created: number;
    updated: number;
    unlinked: number;
    skipped: number;
    errors: Array<{ piJobId: string; error: string }>;
  };
  candidates: {
    imported: number;
    skipped: number;
    errors: Array<{ piPersonId: string; error: string }>;
  };
}

// PI Sync APIs
export const piSyncApi = {
  // Preview PI jobs with match analysis and candidates
  previewJobs: (): Promise<AxiosResponse<{
    piJobs: PIJobPreview[];
    localJobs: Array<{ id: number; title: string; department: string }>;
    totalCandidates: number;
  }>> =>
    api.get('/pi-sync/jobs/preview'),

  // Process job sync decisions (without candidates - for progress tracking)
  processJobs: (decisions: PIJobDecision[]): Promise<AxiosResponse<{
    success: boolean;
    results: PIJobSyncResults;
    jobIdMap: Record<string, number>; // piJobId -> localJobId
  }>> =>
    api.post('/pi-sync/jobs/process', { decisions }),

  // Import a single candidate from PI (for progress tracking)
  importSingleCandidate: (data: {
    piJobId: string;
    piPersonId: string;
    localJobId: number;
  }): Promise<AxiosResponse<{
    success: boolean;
    status: 'imported' | 'already_exists';
    candidate: any;
  }>> =>
    api.post('/pi-sync/candidates/import-one', data),

  // Sync jobs from PI (legacy)
  syncJobs: (): Promise<AxiosResponse<any>> =>
    api.post('/pi-sync/jobs'),

  // Sync single candidate
  syncCandidate: (id: string): Promise<AxiosResponse<any>> =>
    api.post(`/pi-sync/candidates/${id}`),

  // Bulk sync candidates
  syncCandidatesBulk: (candidateIds: number[]): Promise<AxiosResponse<any>> =>
    api.post('/pi-sync/candidates', { candidateIds }),

  // Get candidate insights PDF
  getCandidateInsights: (id: string): Promise<AxiosResponse<any>> =>
    api.get(`/pi-sync/candidates/${id}/insights`, { responseType: 'blob' }),
};

// Reference Types
export interface CandidateReference {
  id: number;
  candidate_id: number;
  name: string;
  company: string | null;
  title: string | null;
  relationship: 'supervisor' | 'coworker' | 'hr' | 'other';
  phone: string | null;
  email: string | null;
  status: 'pending' | 'contacted' | 'completed' | 'unable_to_reach';
  created_by: number | null;
  created_by_first_name?: string;
  created_by_last_name?: string;
  created_at: string;
  updated_at: string;
  check?: ReferenceCheck | null;
}

export interface ReferenceCheck {
  id: number;
  reference_id: number;
  checked_by: number | null;
  checked_by_first_name?: string;
  checked_by_last_name?: string;
  checked_at: string | null;
  contact_method: 'phone' | 'email' | 'in_person';
  dates_employed: string | null;
  title_held: string | null;
  matches_resume: 'yes' | 'no' | 'partial' | null;
  reason_for_leaving: string | null;
  eligible_for_rehire: 'yes' | 'no' | 'with_conditions' | 'policy_prevents';
  rehire_notes: string | null;
  strengths: string | null;
  areas_for_improvement: string | null;
  concerns: string | null;
  overall_recommendation: 'positive' | 'neutral' | 'negative' | null;
  additional_notes: string | null;
  is_draft: boolean;
  created_at: string;
  updated_at: string;
}

export interface ReferenceSummary {
  total: number;
  completed: number;
  pending: number;
  rehireStats: {
    yes: number;
    no: number;
    with_conditions: number;
    policy_prevents: number;
  };
  recommendationStats: {
    positive: number;
    neutral: number;
    negative: number;
  };
  hasDiscrepancies: boolean;
  overallSentiment: 'positive' | 'neutral' | 'negative';
}

// References API
export const referencesApi = {
  // Get all references for a candidate
  getReferences: (candidateId: string): Promise<AxiosResponse<CandidateReference[]>> =>
    api.get(`/candidates/${candidateId}/references`),

  // Get reference summary for a candidate
  getSummary: (candidateId: string): Promise<AxiosResponse<ReferenceSummary>> =>
    api.get(`/candidates/${candidateId}/references/summary`),

  // Create a new reference
  createReference: (candidateId: string, data: {
    name: string;
    company?: string;
    title?: string;
    relationship?: 'supervisor' | 'coworker' | 'hr' | 'other';
    phone?: string;
    email?: string;
  }): Promise<AxiosResponse<CandidateReference>> =>
    api.post(`/candidates/${candidateId}/references`, data),

  // Update a reference
  updateReference: (id: number, data: Partial<CandidateReference>): Promise<AxiosResponse<CandidateReference>> =>
    api.put(`/references/${id}`, data),

  // Delete a reference
  deleteReference: (id: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/references/${id}`),

  // Save reference check results
  saveCheck: (referenceId: number, data: {
    contact_method?: 'phone' | 'email' | 'in_person';
    dates_employed?: string | null;
    title_held?: string | null;
    matches_resume?: 'yes' | 'no' | 'partial' | null;
    reason_for_leaving?: string | null;
    eligible_for_rehire: 'yes' | 'no' | 'with_conditions' | 'policy_prevents';
    rehire_notes?: string | null;
    strengths?: string | null;
    areas_for_improvement?: string | null;
    concerns?: string | null;
    overall_recommendation?: 'positive' | 'neutral' | 'negative' | null;
    additional_notes?: string | null;
    is_draft?: boolean;
  }): Promise<AxiosResponse<ReferenceCheck>> =>
    api.post(`/references/${referenceId}/check`, data),

  // Get reference check results
  getCheck: (referenceId: number): Promise<AxiosResponse<ReferenceCheck>> =>
    api.get(`/references/${referenceId}/check`),
};

// Teams & Team Discovery (PI Design module) API
export const teamsApi = {
  // Teams CRUD
  getAll: (params?: { department?: string; is_active?: string }): Promise<AxiosResponse<Team[]>> =>
    api.get('/teams', { params }),

  getById: (id: string): Promise<AxiosResponse<Team>> =>
    api.get(`/teams/${id}`),

  create: (data: { name: string; department?: string; description?: string }): Promise<AxiosResponse<Team>> =>
    api.post('/teams', data),

  update: (id: string, data: Partial<Team>): Promise<AxiosResponse<Team>> =>
    api.put(`/teams/${id}`, data),

  delete: (id: string): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/teams/${id}`),

  // Members
  addMember: (teamId: string, data: Partial<TeamMember>): Promise<AxiosResponse<TeamMember>> =>
    api.post(`/teams/${teamId}/members`, data),

  addMembersBulk: (teamId: string, members: Partial<TeamMember>[]): Promise<AxiosResponse<TeamMember[]>> =>
    api.post(`/teams/${teamId}/members/bulk`, { members }),

  updateMember: (teamId: string, memberId: string, data: Partial<TeamMember>): Promise<AxiosResponse<TeamMember>> =>
    api.put(`/teams/${teamId}/members/${memberId}`, data),

  removeMember: (teamId: string, memberId: string): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/teams/${teamId}/members/${memberId}`),

  // Team Discovery
  runDiscovery: (teamId: string, strategyType?: StrategyTypeKey): Promise<AxiosResponse<TeamDiscoveryResult>> =>
    api.post(`/teams/${teamId}/discover`, { strategy_type: strategyType }),

  previewDiscovery: (members: Partial<TeamMember>[], strategyType?: StrategyTypeKey): Promise<AxiosResponse<TeamDiscoveryResult>> =>
    api.post('/teams/discover/preview', { members, strategy_type: strategyType }),

  getDiscoveryHistory: (teamId: string): Promise<AxiosResponse<TeamDiscoveryResult[]>> =>
    api.get(`/teams/${teamId}/discovery/history`),

  // Reference data
  getTeamTypes: (): Promise<AxiosResponse<Record<string, { name: string; quadrants: string[]; tagline: string }>>> =>
    api.get('/teams/reference/team-types'),

  getStrategyTypes: (): Promise<AxiosResponse<Record<string, { name: string; description: string }>>> =>
    api.get('/teams/reference/strategy-types'),
};

export default api;
